import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Observable } from 'rxjs';
import {
  PageAndSize,
  Paginated,
} from 'src/app/dashboard/components/overview/models/paginated-overview.model';
import {
  ManureOffer,
  ManureOfferModel,
  OfferExportApiModel,
} from 'src/app/offer/models/manure-offer.model';

import { Environment } from 'src/environments/environment';

import { ManureFilters } from '../../models/manure-filters.model';
import { Status } from '../../models/status.model';

@Injectable({
  providedIn: 'root',
})
export class OfferService {
  private readonly http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  protected baseUrl = this.env.APIM_BASE_URL + '/mestplace-v2-service';

  postRequest(manureRequest: ManureOffer): Observable<ManureOfferModel> {
    return this.http.post<ManureOfferModel>(`${this.baseUrl}/offers`, manureRequest);
  }

  getOffers(
    paginationOptions: PageAndSize,
    filters?: ManureFilters,
  ): Observable<Paginated<ManureOfferModel>> {
    const { pageIndex, pageSize } = paginationOptions;
    const params = {
      pageIndex,
      pageSize,
      ...(filters?.amountFrom ? { amountFrom: filters.amountFrom } : {}),
      ...(filters?.amountTo ? { amountTo: filters.amountTo } : {}),
      ...(filters?.desiredDeliveryPeriodFrom
        ? { desiredDeliveryPeriodFrom: filters.desiredDeliveryPeriodFrom }
        : {}),
      ...(filters?.desiredDeliveryPeriodTo
        ? { desiredDeliveryPeriodTo: filters.desiredDeliveryPeriodTo }
        : {}),
      ...(filters?.distance ? { distance: filters.distance } : {}),
      ...(filters?.isHomogeneous != null ? { isHomogeneous: filters.isHomogeneous } : {}),
      ...(filters?.manureCategory ? { manureCategory: filters.manureCategory } : {}),
      ...(filters?.manureType ? { manureType: filters.manureType } : {}),
      ...{ status: [Status.new, Status.matchRequested] },
    };

    return this.http.get<Paginated<ManureOfferModel>>(`${this.baseUrl}/offers`, { params });
  }

  getOffersForExport() {
    return this.http.get<{ offers: OfferExportApiModel[] }>(`${this.baseUrl}/offers/export`);
  }

  getMyActiveOffers(paginationOptions: PageAndSize): Observable<Paginated<ManureOfferModel>> {
    const { pageIndex, pageSize } = paginationOptions;
    const params = {
      pageIndex,
      pageSize,
      myAdvertisementsOnly: true,
      ...{ status: [Status.new, Status.matchRequested] },
    };
    return this.http.get<Paginated<ManureOfferModel>>(`${this.baseUrl}/offers`, {
      params,
    });
  }

  getMyClosedOffers(paginationOptions: PageAndSize): Observable<Paginated<ManureOfferModel>> {
    const { pageIndex, pageSize } = paginationOptions;
    const params = {
      pageIndex,
      pageSize,
      myAdvertisementsOnly: true,
      ...{ status: Status.matchApproved },
    };

    return this.http.get<Paginated<ManureOfferModel>>(`${this.baseUrl}/offers`, {
      params,
    });
  }

  makeMatch(id: number, intermediary: string): Observable<ManureOfferModel> {
    const matchRequest = { intermediary };

    return this.http.post<ManureOfferModel>(`${this.baseUrl}/offers/${id}/match`, matchRequest);
  }

  deleteOffer(id: number): Observable<HttpResponse<object>> {
    return this.http.delete(`${this.baseUrl}/offers/${id}`, {
      observe: 'response',
      responseType: 'json',
    });
  }
}
